/**
 * InstanceRegistry.ts
 *
 * Central registry mapping (layerId, instanceIndex) <-> entityId across
 * all GPU-instanced Three.js meshes in the Fab Planner 3D scene.
 *
 * Supports raycasting, click selection, hover identification, and metadata lookup.
 */

export type InstanceMetadata = Record<string, unknown>;

export interface RegisteredInstance {
  layerId: string;
  instanceIndex: number;
  entityId: string;
  metadata?: InstanceMetadata;
}

class InstanceRegistryManager {
  /** Map from `${layerId}:${instanceIndex}` to entityId */
  private indexToEntity = new Map<string, string>();

  /** Map from `${layerId}:${entityId}` to instanceIndex */
  private entityToIndex = new Map<string, number>();

  /** Map from `${layerId}:${entityId}` to metadata */
  private entityMetadata = new Map<string, InstanceMetadata>();

  /** Track all entities registered for a given layerId */
  private layerEntities = new Map<string, Set<string>>();

  public register(
    layerId: string,
    instanceIndex: number,
    entityId: string,
    metadata?: InstanceMetadata
  ): void {
    const indexKey = `${layerId}:${instanceIndex}`;
    const entityKey = `${layerId}:${entityId}`;

    this.indexToEntity.set(indexKey, entityId);
    this.entityToIndex.set(entityKey, instanceIndex);

    if (metadata) {
      this.entityMetadata.set(entityKey, metadata);
    }

    let entities = this.layerEntities.get(layerId);
    if (!entities) {
      entities = new Set<string>();
      this.layerEntities.set(layerId, entities);
    }
    entities.add(entityId);
  }

  public getEntityId(layerId: string, instanceIndex: number): string | undefined {
    return this.indexToEntity.get(`${layerId}:${instanceIndex}`);
  }

  public getInstanceIndex(layerId: string, entityId: string): number | undefined {
    return this.entityToIndex.get(`${layerId}:${entityId}`);
  }

  public getMetadata(layerId: string, entityId: string): InstanceMetadata | undefined {
    return this.entityMetadata.get(`${layerId}:${entityId}`);
  }

  public clear(layerId: string): void {
    const entities = this.layerEntities.get(layerId);
    if (entities) {
      for (const entityId of entities) {
        const entityKey = `${layerId}:${entityId}`;
        const idx = this.entityToIndex.get(entityKey);
        if (idx !== undefined) {
          this.indexToEntity.delete(`${layerId}:${idx}`);
        }
        this.entityToIndex.delete(entityKey);
        this.entityMetadata.delete(entityKey);
      }
      this.layerEntities.delete(layerId);
    }
  }

  public handleClick(
    layerId: string,
    event: { instanceId?: number; stopPropagation?: () => void },
    onSelect?: (entityId: string) => void
  ): boolean {
    if (event.instanceId === undefined) return false;
    const entityId = this.getEntityId(layerId, event.instanceId);
    if (entityId) {
      event.stopPropagation?.();
      onSelect?.(entityId);
      return true;
    }
    return false;
  }
}

export const instanceRegistry = new InstanceRegistryManager();
export default instanceRegistry;
